import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { 
  PlusIcon, 
  PencilSquareIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  PrinterIcon,
  WifiIcon,
  XCircleIcon,
  BoltIcon,
  CogIcon,
  DocumentTextIcon,
  ServerIcon,
  ExclamationTriangleIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { 
  getPrinters,
  createPrinter, 
  updatePrinter, 
  deletePrinter,
  testPrinter,
  setDefaultPrinter,
  getKitchens
} from '../api/api';
import { useConfirm } from '../context/ConfirmContext';
import Modal from '../components/Modal';
import AppIconButton from '../components/ui/AppIconButton';
import { PageHeroBand, SecondaryButton } from '../components/ui';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Divider,
  Button,
  Tab,
  Tabs,
  Avatar,
  CircularProgress
} from '@mui/material';

interface Printer {
  id: number;
  name: string;
  ipAddress: string;
  port: number;
  type: string;
  isActive: boolean;
  isDefault: boolean;
  lastHeartbeat?: string;
  kitchenName?: string;
  printCount?: number;
  createdAt: string;
}

interface Kitchen {
  id: number;
  name: string;
  type: string;
  isActive: boolean;
}

const PRINTER_TYPES = [
  { value: 'THERMAL', label: 'Thermal Printer' },
  { value: 'IMPACT', label: 'Impact Printer' },
  { value: 'LASER', label: 'Laser Printer' },
  { value: 'NETWORK', label: 'Network Printer' }
];

const PrinterManagement: React.FC = () => {
  const { theme } = useTheme();
  const confirm = useConfirm();
  
  // State management
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<Printer | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    ipAddress: '',
    port: 9100,
    type: 'THERMAL',
    isActive: true,
    isDefault: false
  });
  
  // Error and success states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Tab state for modal
  const [modalTab, setModalTab] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [printersData, kitchensData] = await Promise.all([
        getPrinters(),
        getKitchens()
      ]);
      
      setPrinters(printersData);
      setKitchens(kitchensData);
    } catch (err: unknown) {
      console.error('Error fetching data:', err);
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    setSuccess('Data refreshed successfully');
  };

  const handleOpenModal = (printer?: Printer) => {
    if (printer) {
      setEditingPrinter(printer);
      setFormData({
        name: printer.name,
        ipAddress: printer.ipAddress,
        port: printer.port,
        type: printer.type,
        isActive: printer.isActive,
        isDefault: printer.isDefault
      });
    } else {
      setEditingPrinter(null);
      setFormData({
        name: '',
        ipAddress: '',
        port: 9100,
        type: 'THERMAL',
        isActive: true,
        isDefault: false
      });
    }
    setModalTab(0);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPrinter(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const printerData = {
        name: formData.name,
        ipAddress: formData.ipAddress,
        port: formData.port,
        type: formData.type,
        isActive: formData.isActive,
        isDefault: formData.isDefault
      };

      if (editingPrinter) {
        await updatePrinter(editingPrinter.id, printerData);
        setSuccess('Printer updated successfully');
      } else {
        await createPrinter(printerData);
        setSuccess('Printer created successfully');
      }

      handleCloseModal();
      fetchData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to save printer');
    }
  };

  const handleDelete = async (printer: Printer) => {
    const result = await confirm({
      title: 'Delete Printer',
      description: `Are you sure you want to delete ${printer.name}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (result) {
      try {
        await deletePrinter(printer.id);
        setSuccess('Printer deleted successfully');
        fetchData();
      } catch (err: unknown) {
        const error = err as { response?: { data?: { error?: string } } };
        setError(error.response?.data?.error || 'Failed to delete printer');
      }
    }
  };

  const handleTestPrinter = async (printer: Printer) => {
    setTesting(printer.id);
    try {
      await testPrinter(printer.id);
      setSuccess(`Test print sent to ${printer.name}`);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to test printer');
    } finally {
      setTesting(null);
    }
  };

  const handleSetDefault = async (printer: Printer) => {
    try {
      await setDefaultPrinter(printer.id);
      setSuccess(`${printer.name} set as default printer`);
      fetchData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to set default printer');
    }
  };

  const getStatusColor = (isActive: boolean, lastHeartbeat?: string) => {
    if (!isActive) return theme.danger;
    if (!lastHeartbeat) return theme.warning;
    
    const lastBeat = new Date(lastHeartbeat);
    const now = new Date();
    const diffMinutes = (now.getTime() - lastBeat.getTime()) / (1000 * 60);
    
    if (diffMinutes < 5) return theme.success;
    if (diffMinutes < 30) return theme.warning;
    return theme.danger;
  };

  // Filtering and pagination
  const filteredPrinters = printers.filter(printer => {
    const matchesSearch = printer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         printer.ipAddress.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || printer.type === filterType;
    
    const matchesStatus = filterStatus === 'all' ||
                         (filterStatus === 'active' && printer.isActive) ||
                         (filterStatus === 'inactive' && !printer.isActive);
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const paginatedPrinters = filteredPrinters.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Stats
  const stats = {
    total: printers.length,
    active: printers.filter(p => p.isActive).length,
    online: printers.filter(p => {
      if (!p.lastHeartbeat) return false;
      const diffMinutes = (new Date().getTime() - new Date(p.lastHeartbeat).getTime()) / (1000 * 60);
      return diffMinutes < 5;
    }).length
  };

  // Mirrors the Finance module's summary-card shell so both read as one system.
  const cardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: '12px',
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
  };

  // "Online now" is THE number for this page and lives in the hero band.
  const summaryCards = [
    { label: 'Total Printers', value: stats.total },
    { label: 'Active', value: stats.active },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: theme.background, minHeight: '100vh' }}>
      {/* Header — confident-shell hero band, same as the Finance module */}
      <PageHeroBand
        eyebrow="Settings · Printers"
        title="Printer Management"
        heroLabel="Online now"
        heroValue={String(stats.online)}
        actions={<Box sx={{ display: 'flex', gap: 1, flexShrink: 0,
          '& .MuiButton-root': { backgroundColor: 'color-mix(in srgb, var(--band-text) 10%, transparent)', color: 'var(--band-text)', borderColor: 'color-mix(in srgb, var(--band-text) 25%, transparent)', '&:hover': { backgroundColor: 'color-mix(in srgb, var(--band-text) 18%, transparent)', borderColor: 'color-mix(in srgb, var(--band-text) 40%, transparent)' } } }}>
          <SecondaryButton
            onClick={handleRefresh}
            disabled={refreshing}
            startIcon={<ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />}
            sx={{ fontSize: '0.813rem', px: 1.5 }}
          >
            Refresh
          </SecondaryButton>
          <SecondaryButton
            onClick={() => handleOpenModal()}
            startIcon={<PlusIcon className="h-5 w-5" />}
            data-testid="add-printer-button"
            sx={{ fontSize: '0.813rem', px: 1.5 }}
          >
            Add Printer
          </SecondaryButton>
        </Box>}
      />

      {/* Summary cards — white surface, honest zero state (see FinanceDashboard) */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        {summaryCards.map((card) => (
          <Grid item xs={6} sm={6} md={6} key={card.label}>
            <Paper elevation={0} sx={cardSx}>
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--text-tertiary)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                  letterSpacing: '0.05em',
                }}
              >
                {card.label}
              </Typography>
              <Typography
                sx={{
                  fontWeight: card.value === 0 ? 600 : 700,
                  color: card.value === 0 ? 'var(--text-tertiary)' : 'var(--text-primary)',
                  fontSize: { xs: '1.35rem', sm: '1.75rem' },
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.15,
                  mt: 0.25,
                }}
              >
                {card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, fontSize: { xs: '0.875rem', md: '1rem' } }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 3, fontSize: { xs: '0.875rem', md: '1rem' } }} 
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Table Section */}
      <Paper sx={{ 
        backgroundColor: theme.components?.card?.background, 
        border: `1px solid ${theme.components?.card?.border}`,
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        {/* Table Header */}
        <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.components?.card?.border}` }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                placeholder="Search printers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MagnifyingGlassIcon className="h-5 w-5" style={{ color: theme.gray?.[600] }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={6} md={2}>
              <FormControl size="small" fullWidth>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {PRINTER_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} md={2}>
              <FormControl size="small" fullWidth>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="inactive">Inactive Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

          </Grid>
        </Box>

        {/* Loading Bar */}
        {(loading || refreshing) && <LinearProgress />}

        {/* Responsive Table */}
        <TableContainer sx={{
          width: '100%',
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: { xs: '800px', sm: '900px', md: '1000px' }
          }
        }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: theme.text, 
                  minWidth: { xs: '120px', md: '160px' },
                  whiteSpace: 'nowrap'
                }}>Printer</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: theme.text, 
                  minWidth: { xs: '100px', md: '120px' },
                  whiteSpace: 'nowrap'
                }}>IP Address</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: theme.text, 
                  minWidth: { xs: '70px', md: '80px' },
                  whiteSpace: 'nowrap'
                }}>Status</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: theme.text, 
                  minWidth: { xs: '60px', md: '80px' },
                  whiteSpace: 'nowrap'
                }}>Type</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: theme.text, 
                  minWidth: { xs: '70px', md: '100px' },
                  whiteSpace: 'nowrap'
                }}>Kitchen</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: theme.text, 
                  minWidth: { xs: '80px', md: '100px' },
                  whiteSpace: 'nowrap'
                }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !refreshing ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : paginatedPrinters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" sx={{ color: theme.gray?.[600] }}>
                      No printers found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPrinters.map((printer) => {
                  const statusColor = getStatusColor(printer.isActive, printer.lastHeartbeat);
                  
                  return (
                    <TableRow key={printer.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                          <Avatar sx={{ backgroundColor: theme.gray?.[100], width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                            <PrinterIcon className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: theme.gray?.[700] }} />
                          </Avatar>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  fontWeight: 600, 
                                  color: theme.text,
                                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                }}
                              >
                                {printer.name}
                              </Typography>
                              {printer.isDefault && (
                                <Chip 
                                  label="DEFAULT" 
                                  size="small"
                                  sx={{ 
                                    // Tokens, not theme.primary — see the
                                    // action-colour-contract test.
                                    backgroundColor: 'color-mix(in srgb, var(--action) 12%, transparent)',
                                    color: 'var(--action)',
                                    fontWeight: 700,
                                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                    height: { xs: '16px', sm: '20px' }
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            color: theme.text,
                            fontWeight: 500
                          }}
                        >
                          {printer.ipAddress}:{printer.port}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: { xs: 6, sm: 8 },
                              height: { xs: 6, sm: 8 },
                              borderRadius: '50%',
                              backgroundColor: statusColor,
                              animation: statusColor === theme.success ? 'pulse 2s infinite' : 'none',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                                '100%': { opacity: 1 }
                              }
                            }}
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: statusColor, 
                              fontWeight: 600,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                          >
                            {printer.isActive ? 'Active' : 'Inactive'}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          label={printer.type} 
                          size="small"
                          sx={{ 
                            backgroundColor: theme.gray?.[100], 
                            color: theme.text,
                            fontWeight: 600,
                            fontSize: { xs: '0.6rem', sm: '0.75rem' }
                          }}
                        />
                      </TableCell>
                      
                      <TableCell>
                        {printer.kitchenName ? (
                          <Chip 
                            label={printer.kitchenName} 
                            size="small"
                            sx={{ 
                              backgroundColor: theme.secondary + '20', 
                              color: theme.secondary,
                              fontWeight: 600,
                              fontSize: { xs: '0.6rem', sm: '0.75rem' }
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: theme.gray?.[500], fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            Not assigned
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, justifyContent: 'flex-end' }}>
                          <AppIconButton
                            icon={testing === printer.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <PlayIcon className="h-4 w-4" />
                            )}
                            tooltip="Test Print"
                            variant="ghost"
                            size="small"
                            onClick={() => handleTestPrinter(printer)}
                            disabled={!printer.isActive || testing === printer.id}
                            sx={{
                              color: theme.accent,
                              backgroundColor: 'transparent',
                              width: { xs: '28px', sm: '32px' },
                              height: { xs: '28px', sm: '32px' },
                              '&:hover': { backgroundColor: 'transparent' },
                            }}
                          />
                          
                          {!printer.isDefault && (
                            <AppIconButton
                              icon={<CogIcon className="h-4 w-4" />}
                              tooltip="Set as Default"
                              variant="ghost"
                              size="small"
                              onClick={() => handleSetDefault(printer)}
                              sx={{
                                color: theme.warning,
                                backgroundColor: 'transparent',
                                width: { xs: '28px', sm: '32px' },
                                height: { xs: '28px', sm: '32px' },
                                '&:hover': { backgroundColor: 'transparent' },
                              }}
                            />
                          )}
                          
                          <AppIconButton
                            icon={<PencilSquareIcon className="h-4 w-4" />}
                            tooltip="Edit"
                            variant="ghost"
                            size="small"
                            onClick={() => handleOpenModal(printer)}
                            sx={{
                              color: theme.primary,
                              backgroundColor: 'transparent',
                              width: { xs: '28px', sm: '32px' },
                              height: { xs: '28px', sm: '32px' },
                              '&:hover': { backgroundColor: 'transparent' },
                            }}
                          />
                          
                          <AppIconButton
                            icon={<TrashIcon className="h-4 w-4" />}
                            tooltip="Delete"
                            variant="ghost"
                            size="small"
                            onClick={() => handleDelete(printer)}
                            disabled={printer.isDefault}
                            sx={{
                              color: theme.danger,
                              backgroundColor: 'transparent',
                              width: { xs: '28px', sm: '32px' },
                              height: { xs: '28px', sm: '32px' },
                              '&:hover': { backgroundColor: 'transparent' },
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredPrinters.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexWrap: 'wrap',
              gap: 1
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }
          }}
        />
      </Paper>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingPrinter ? 'Edit Printer' : 'Add New Printer'}
        maxWidth="md"
      >
        <Box sx={{ mt: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Printer Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Printer Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label="Printer Type"
                  >
                    {PRINTER_TYPES.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                    }
                    label="Active Status"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isDefault}
                        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                      />
                    }
                    label="Set as Default"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  label="IP Address"
                  value={formData.ipAddress}
                  onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Port"
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button 
                onClick={handleCloseModal} 
                variant="outlined"
                sx={{
                  textTransform: 'none !important',
                  fontWeight: '600 !important',
                  px: 3,
                  py: 1,
                  borderColor: 'var(--border-default) !important',
                  color: 'var(--text-tertiary) !important',
                  '&:hover': {
                    borderColor: 'var(--border-strong) !important',
                    backgroundColor: 'var(--bg-sunken) !important',
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  textTransform: 'none !important',
                  fontWeight: '600 !important',
                  px: 3,
                  py: 1,
                  backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
                  color: 'var(--text-link) !important',
                  borderWidth: '1px !important',
                  borderStyle: 'solid !important',
                  borderColor: 'rgba(96, 165, 250, 0.31) !important',
                  boxShadow: 'none !important',
                  '&:hover': {
                    backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                    borderColor: 'rgba(96, 165, 250, 0.4) !important',
                    boxShadow: 'none !important',
                  }
                }}
              >
                {editingPrinter ? 'Update' : 'Create'} Printer
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </Box>
  );
};

export default PrinterManagement; 